/* eslint-disable */
import { useState, useEffect, useRef, useMemo } from "react";
import SearchBox from "../components/SearchBox";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData";
import { useDateTimeFormatter } from "../hooks/useDateTimeFormatter";
import SkeletonPageAccessTable from "../components/skeletons/SkeletonPageAccessTable";
import { apiClient } from "../auth/apiClient";
import { Link } from "react-router-dom";
import SelectorBackground from "../components/SelectorBackground";
import "./pageAccess.css";

const PERMISSION_ROUTES = {
  steam: "/steam",
  country: "/country",
  "mind-share": "/mindshare",
  scorecards: "/scorecards",
  twitch: "/twitch",
  "tik-tok": "/tiktok",
  mobile: "/mobile",
  roblox: "/roblox",
  calendar: "/calendar",
  discord: "/discord",
  youtube: "/youtube",
  xbox: "/xbox",
};

function PageAccess() {
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // track in-flight saves to avoid race/double toggles
  const pending = useRef(new Set()); // key = `${userId}:${permissionId}`

  const {
    items: sortedUsers,
    sortConfig,
    requestSort,
  } = useSortableData(users);

  const {
    formatLastSeenDateTime,
    formatLastLoginDateTime,
    formatDetailedDateTime,
  } = useDateTimeFormatter();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [permsRes, usersRes] = await Promise.all([
          apiClient.get("/permissions"),
          apiClient.get("/users"),
        ]);

        const perms = await permsRes.json();
        const usrs = await usersRes.json();

        // defensive: ensure arrays
        setPermissions(Array.isArray(perms) ? perms : []);
        setUsers(
          Array.isArray(usrs)
            ? usrs.map((u) => ({
                ...u,
                permissions: Array.isArray(u.permissions) ? u.permissions : [],
              }))
            : []
        );
      } catch (error) {
        console.error("Error fetching data:", error);
        setUsers([]);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return sortedUsers;
    return sortedUsers.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
    );
  }, [sortedUsers, searchTerm]);

  const toggleAccess = async (userId, permissionId) => {
    const key = `${userId}:${permissionId}`;
    if (pending.current.has(key)) return; // avoid double toggle while saving

    const user = users.find((u) => u.id === userId);
    if (!user) return;

    const hadPermission = user.permissions.includes(permissionId);
    const nextChecked = !hadPermission;

    // optimistic UI update
    setUsers((prev) =>
      prev.map((u) => {
        if (u.id !== userId) return u;
        return {
          ...u,
          permissions: nextChecked
            ? [...u.permissions, permissionId]
            : u.permissions.filter((p) => p !== permissionId),
        };
      })
    );

    pending.current.add(key);
    const url = `/users/${userId}/permissions/${permissionId}`;

    try {
      // === IMPORTANT ===
      // We always send a valid payload for PUT so we never post "undefined".
      // If your API contract is "PUT to grant, DELETE to revoke", this handles both.
      if (nextChecked) {
        const res = await apiClient.put(url, { granted: true });
        if (!res.ok) throw new Error(`PUT ${url} -> ${res.status}`);
      } else {
        const res = await apiClient.delete(url);
        if (!res.ok) throw new Error(`DELETE ${url} -> ${res.status}`);
      }
    } catch (err) {
      console.error("Failed to persist permission change:", err);

      // rollback UI
      setUsers((prev) =>
        prev.map((u) => {
          if (u.id !== userId) return u;
          return {
            ...u,
            permissions: hadPermission
              ? Array.from(new Set([...u.permissions, permissionId]))
              : u.permissions.filter((p) => p !== permissionId),
          };
        })
      );
      // (Optional) surface UI toast/snackbar
    } finally {
      pending.current.delete(key);
    }
  };

  return (
    <div className="page-access" style={styles.container}>
      <SelectorBackground />
      <h1 style={styles.header}>Page Access</h1>

      <SearchBox
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search by name or email..."
      />

      {loading ? (
        <SkeletonPageAccessTable
          permissionCount={permissions.length > 0 ? permissions.length : 9}
          rowCount={5}
          styles={styles}
        />
      ) : (
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            {permissions.map((permission) => (
              <col key={permission.id} style={{ width: `${64 / Math.max(permissions.length, 1)}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <SortableHeader
                sortKey="name"
                sortConfig={sortConfig}
                onSort={requestSort}
              >
                Name
              </SortableHeader>

              <SortableHeader
                sortKey="email"
                sortConfig={sortConfig}
                onSort={requestSort}
              >
                Email
              </SortableHeader>

              <SortableHeader
                sortKey="last_login"
                sortConfig={sortConfig}
                onSort={requestSort}
                align="center"
              >
                Last Login
              </SortableHeader>

              <SortableHeader
                sortKey="last_seen"
                sortConfig={sortConfig}
                onSort={requestSort}
                align="center"
              >
                Last Seen
              </SortableHeader>

              {permissions.map((pObj) => (
                <SortableHeader key={pObj.id} align="center" sortable={false}>
                  {PERMISSION_ROUTES[pObj.id] ? (
                    <Link to={PERMISSION_ROUTES[pObj.id]} style={styles.permissionLink} title={`Open ${pObj.name}`}>
                      {pObj.name}
                    </Link>
                  ) : pObj.name}
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.id}>
                <td style={styles.td}>{u.name}</td>
                <td style={styles.td}>{u.email}</td>
                <td
                  style={styles.tdCenter}
                  title={formatDetailedDateTime(u.last_login)}
                >
                  {formatLastLoginDateTime(u.last_login)}
                </td>
                <td
                  style={styles.tdCenter}
                  title={formatDetailedDateTime(u.last_seen)}
                >
                  {formatLastSeenDateTime(u.last_seen)}
                </td>
                {permissions.map((pObj) => {
                  const checked = u.permissions.includes(pObj.id);
                  const lockKey = `${u.id}:${pObj.id}`;
                  const isSaving = pending.current.has(lockKey);
                  return (
                    <td key={pObj.id} style={styles.tdCenter}>
                      <div style={styles.centerContent}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={isSaving}
                          onChange={() => toggleAccess(u.id, pObj.id)}
                          style={styles.checkbox}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  container: {
    color: "#fff",
    padding: 20,
    fontFamily: '"Jura", sans-serif',
  },
  header: {
    margin: "0 0 20px",
    fontSize: 34,
    lineHeight: "40px",
    fontWeight: 1000,
    letterSpacing: 0,
    color: "#fff",
  },
  table: { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  permissionLink: {
    display: "block",
    color: "#e8edf2",
    textDecoration: "none",
    fontSize: 10,
    lineHeight: "12px",
    fontWeight: 800,
    whiteSpace: "nowrap",
    transition: "color 140ms ease",
  },
  td: {
    padding: "8px 6px",
    borderBottom: "1px solid #222",
    textAlign: "left",
    fontSize: "12px",
    overflowWrap: "anywhere",
  },
  tdCenter: {
    padding: "8px 3px",
    borderBottom: "1px solid #222",
    textAlign: "center",
    fontSize: "11px",
    overflowWrap: "anywhere",
  },
  centerContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  checkbox: {
    width: 16,
    height: 16,
    cursor: "pointer",
    accentColor: "#3fa9f5",
  },
};

export default PageAccess;
